// 📈 유입경로 통계 페이지 - 마케팅 채널별 신청 집계/차트 + 추적 링크 관리
// 프리미엄 히어로 헤더 + 3개 탭 (성과 대시보드 / 채널별 상세 / 추적 링크 관리)
import { useMemo, useState } from 'react';
import {
  Box,
  Container,
  Grid,
  Stack,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  Paper,
  Tabs,
  Tab,
  Fade,
} from '@mui/material';
import {
  Today as TodayIcon,
  EmojiEvents as EmojiEventsIcon,
  TrackChanges as TrackChangesIcon,
  Hub as HubIcon,
  Insights as InsightsIcon,
  TableChart as TableChartIcon,
  AddLink as AddLinkIcon,
  Storefront as StorefrontIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ko } from 'date-fns/locale/ko';
import dayjs from 'dayjs';

import { useApplications } from './hooks/useApplications';
import { useInflowStats } from './hooks/useInflowStats';
import { isDateInRange } from '../../utils/dateRangeFilter';
import { DATE_RANGE_OPTIONS } from '../../constants/dateRangeOptions';
import InflowKpiCard from './components/inflow/InflowKpiCard';
import InflowShareChart from './components/inflow/InflowShareChart';
import InflowDailyChart from './components/inflow/InflowDailyChart';
import InflowChannelTable from './components/inflow/InflowChannelTable';
import InflowLinkManager from './components/inflow/InflowLinkManager';

/** 일별 차트 X축 최대 표시 일수 (스택 막대 가독성 유지) */
const MAX_CHART_DAYS = 92;

/** 추적 링크 이용 흐름 안내 단계 */
const FLOW_STEPS = [
  {
    icon: <AddLinkIcon />,
    title: '① 추적 링크 생성',
    desc: '아래에서 채널별 링크를 만들어 저장',
    color: '#1976d2',
  },
  {
    icon: <StorefrontIcon />,
    title: '② 배너에 적용',
    desc: '지마켓·쿠팡 등 배너의 랜딩 URL을 생성한 링크로 교체',
    color: '#7c3aed',
  },
  {
    icon: <InsightsIcon />,
    title: '③ 자동 집계',
    desc: '그 링크로 들어온 신청이 채널별로 자동 분류·집계',
    color: '#16a34a',
  },
];

/** 탭 공통 스타일 (알약형) */
const pillTabSx = {
  minHeight: 40,
  px: { xs: 1.6, md: 2.4 },
  mr: 0.5,
  borderRadius: 99,
  fontSize: 13.5,
  fontWeight: 700,
  textTransform: 'none' as const,
  color: '#64748b',
  transition: 'all 0.2s ease',
  '& .MuiTab-iconWrapper': { mr: 0.7 },
  '&:hover': { color: '#1e293b', bgcolor: 'rgba(255,255,255,0.6)' },
  '&.Mui-selected': {
    bgcolor: '#fff',
    color: '#1e293b',
    boxShadow: '0 2px 10px rgba(15, 23, 42, 0.1)',
  },
};

const InflowStatsPage = () => {
  const { applications, loading, error } = useApplications();

  const [tab, setTab] = useState(0);
  const [dateRange, setDateRange] = useState('이번 달');
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);

  // 기간 필터 적용 (KPI/도넛/표 대상)
  const filteredApplications = useMemo(
    () =>
      applications.filter((app) => {
        const createdAt = app.created_at?.seconds
          ? new Date(app.created_at.seconds * 1000)
          : new Date();
        return isDateInRange(createdAt, dateRange, startDate, endDate);
      }),
    [applications, dateRange, startDate, endDate],
  );

  // 일별 차트 X축 범위
  const [chartStart, chartEnd] = useMemo(() => {
    const now = dayjs();
    if (dateRange === '최근 7일') {
      return [now.subtract(6, 'day').startOf('day'), now];
    }
    if (dateRange === '이번 달') {
      return [now.startOf('month'), now];
    }
    if (dateRange === '사용자 지정' && startDate && endDate) {
      let start = dayjs(startDate).startOf('day');
      const end = dayjs(endDate).endOf('day');
      if (end.diff(start, 'day') > MAX_CHART_DAYS) {
        start = end.subtract(MAX_CHART_DAYS, 'day').startOf('day');
      }
      return [start, end];
    }
    // '전체'는 일별 차트만 최근 30일로 제한 (KPI/도넛/표는 전체 기준)
    return [now.subtract(29, 'day').startOf('day'), now];
  }, [dateRange, startDate, endDate]);

  const stats = useInflowStats(filteredApplications, chartStart, chartEnd);

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 5 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          데이터를 불러오는 중 오류가 발생했습니다. 네트워크 연결을 확인해주세요.
        </Alert>
      </Container>
    );
  }

  if (loading) {
    return (
      <Box textAlign="center" mt={6}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 } }}>

      {/* ═══════════ 프리미엄 히어로 헤더 ═══════════ */}
      <Paper
        elevation={0}
        sx={{
          mb: 2.5,
          p: { xs: 2.5, md: 3.5 },
          borderRadius: 4,
          position: 'relative',
          overflow: 'hidden',
          color: '#fff',
          background: 'linear-gradient(135deg, #0f172a 0%, #1e2a4a 55%, #312e81 100%)',
          // 은은한 장식 광원
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -110,
            right: -70,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(99,102,241,0.35) 0%, transparent 70%)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -130,
            left: '32%',
            width: 260,
            height: 260,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(14,165,233,0.18) 0%, transparent 70%)',
          },
        }}
      >
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', md: 'center' }}
          spacing={2}
          sx={{ position: 'relative', zIndex: 1 }}
        >
          <Box>
            <Typography sx={{ fontSize: { xs: '1.15rem', md: '1.35rem' }, fontWeight: 800, letterSpacing: '-0.5px' }}>
              📈 유입경로 통계
            </Typography>
            <Typography sx={{ fontSize: '0.82rem', color: 'rgba(255,255,255,0.72)', mt: 0.5 }}>
              지마켓 · 옥션 · 네이버 · 쿠팡 등 어떤 채널을 타고 신청이 들어오는지 추적하고 집계합니다
            </Typography>
            <Stack direction="row" spacing={1} mt={1.5}>
              <Chip
                label={`기간 내 ${stats.total.toLocaleString()}건`}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: 12, color: '#fff', height: 26,
                  bgcolor: 'rgba(255,255,255,0.14)', border: '1px solid rgba(255,255,255,0.2)',
                }}
              />
              <Chip
                label={`추적률 ${stats.trackingRate}%`}
                size="small"
                sx={{
                  fontWeight: 700, fontSize: 12, color: '#86efac', height: 26,
                  bgcolor: 'rgba(22,163,74,0.22)', border: '1px solid rgba(134,239,172,0.25)',
                }}
              />
            </Stack>
          </Box>

          {/* 기간 알약 필터 */}
          <Stack
            direction="row"
            spacing={0.6}
            sx={{
              p: 0.6,
              borderRadius: 99,
              bgcolor: 'rgba(255,255,255,0.08)',
              border: '1px solid rgba(255,255,255,0.12)',
              flexWrap: 'wrap',
            }}
          >
            {DATE_RANGE_OPTIONS.map((option) => {
              const selected = dateRange === option;
              return (
                <Box
                  key={option}
                  onClick={() => setDateRange(option)}
                  sx={{
                    px: 1.8, py: 0.7, borderRadius: 99, cursor: 'pointer',
                    fontSize: 12.5, fontWeight: 700, userSelect: 'none',
                    whiteSpace: 'nowrap', transition: 'all 0.2s ease',
                    bgcolor: selected ? '#fff' : 'transparent',
                    color: selected ? '#1e293b' : 'rgba(255,255,255,0.8)',
                    boxShadow: selected ? '0 2px 10px rgba(0,0,0,0.25)' : 'none',
                    '&:hover': { bgcolor: selected ? '#fff' : 'rgba(255,255,255,0.14)' },
                  }}
                >
                  {option}
                </Box>
              );
            })}
          </Stack>
        </Stack>
      </Paper>

      {/* 사용자 지정 날짜 선택 */}
      {dateRange === '사용자 지정' && (
        <Paper elevation={0} sx={{
          mb: 2.5, p: 2, borderRadius: 3, border: '1px solid #e8edf4',
        }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ko}>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }}>
              <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#475569', mr: 0.5 }}>
                조회 기간
              </Typography>
              <DatePicker
                label="시작일"
                value={startDate}
                onChange={setStartDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
              <DatePicker
                label="종료일"
                value={endDate}
                onChange={setEndDate}
                slotProps={{ textField: { size: 'small', sx: { minWidth: 160 } } }}
              />
            </Stack>
          </LocalizationProvider>
        </Paper>
      )}

      {/* ═══════════ 알약형 탭 ═══════════ */}
      <Box sx={{
        display: 'inline-flex', maxWidth: '100%',
        p: 0.6, mb: 3, borderRadius: 99,
        bgcolor: '#eef2f7', border: '1px solid #e2e8f0',
      }}>
        <Tabs
          value={tab}
          onChange={(_, value) => setTab(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          TabIndicatorProps={{ sx: { display: 'none' } }}
          sx={{ minHeight: 0, '& .MuiTabs-flexContainer': { alignItems: 'center' } }}
        >
          <Tab disableRipple icon={<InsightsIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="성과 대시보드" sx={pillTabSx} />
          <Tab disableRipple icon={<TableChartIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="채널별 상세" sx={pillTabSx} />
          <Tab disableRipple icon={<AddLinkIcon sx={{ fontSize: 17 }} />} iconPosition="start" label="추적 링크 관리" sx={pillTabSx} />
        </Tabs>
      </Box>

      {/* ═══════════ 탭 1: 성과 대시보드 ═══════════ */}
      {tab === 0 && (
        <Fade in timeout={350}>
          <Box role="tabpanel">
            {stats.activeChannelCount === 0 && (
              <Alert severity="info" sx={{ mb: 2.5, borderRadius: 2.5 }}>
                아직 마케팅 채널로 집계된 신청이 없습니다. <b>[추적 링크 관리]</b> 탭에서 링크를 만들어
                배너에 적용하면 그 이후 신청부터 채널별로 집계됩니다.
              </Alert>
            )}

            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item xs={6} md={3}>
                <InflowKpiCard
                  icon={<TodayIcon />}
                  label="기간 내 총 신청"
                  value={`${stats.total.toLocaleString()}건`}
                  sub={`기준: ${dateRange}`}
                  accentColor="#1976d2"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InflowKpiCard
                  icon={<EmojiEventsIcon />}
                  label="최다 유입 채널"
                  value={stats.topChannel ? stats.topChannel.label : '-'}
                  sub={stats.topChannel ? `${stats.topChannel.count.toLocaleString()}건 유입` : '집계된 채널 없음'}
                  accentColor={stats.topChannel?.color ?? '#94a3b8'}
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InflowKpiCard
                  icon={<TrackChangesIcon />}
                  label="유입 추적률"
                  value={`${stats.trackingRate}%`}
                  sub="수동등록 건 제외"
                  accentColor="#16a34a"
                />
              </Grid>
              <Grid item xs={6} md={3}>
                <InflowKpiCard
                  icon={<HubIcon />}
                  label="활성 유입 채널"
                  value={`${stats.activeChannelCount}개`}
                  sub="기간 내 유입이 발생한 채널"
                  accentColor="#7c3aed"
                />
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <InflowShareChart data={stats.share} />
              </Grid>
              <Grid item xs={12} md={7}>
                <InflowDailyChart data={stats.daily} series={stats.dailySeries} />
              </Grid>
            </Grid>
          </Box>
        </Fade>
      )}

      {/* ═══════════ 탭 2: 채널별 상세 ═══════════ */}
      {tab === 1 && (
        <Fade in timeout={350}>
          <Box role="tabpanel">
            <InflowChannelTable rows={stats.table} />
          </Box>
        </Fade>
      )}

      {/* ═══════════ 탭 3: 추적 링크 관리 ═══════════ */}
      {tab === 2 && (
        <Fade in timeout={350}>
          <Box role="tabpanel">
            {/* 이용 흐름 안내 */}
            <Paper elevation={0} sx={{
              p: { xs: 2, md: 2.5 }, mb: 3, borderRadius: 3,
              border: '1px solid #e8edf4',
              background: 'linear-gradient(135deg, #f8fafc 0%, #fff 60%)',
            }}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                spacing={{ xs: 1.5, md: 0 }}
                alignItems={{ xs: 'flex-start', md: 'center' }}
              >
                {FLOW_STEPS.map((step, idx) => (
                  <Stack
                    key={step.title}
                    direction="row"
                    alignItems="center"
                    spacing={0}
                    sx={{ flex: 1, width: '100%' }}
                  >
                    <Stack direction="row" alignItems="center" spacing={1.2} sx={{ flex: 1 }}>
                      <Box sx={{
                        width: 36, height: 36, borderRadius: '10px', flexShrink: 0,
                        bgcolor: `${step.color}12`, color: step.color,
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        '& svg': { fontSize: 20 },
                      }}>
                        {step.icon}
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
                          {step.title}
                        </Typography>
                        <Typography sx={{ fontSize: 11.5, color: '#64748b', lineHeight: 1.4 }}>
                          {step.desc}
                        </Typography>
                      </Box>
                    </Stack>
                    {idx < FLOW_STEPS.length - 1 && (
                      <ChevronRightIcon sx={{
                        color: '#cbd5e1', mx: 1, flexShrink: 0,
                        display: { xs: 'none', md: 'block' },
                      }} />
                    )}
                  </Stack>
                ))}
              </Stack>
            </Paper>

            <InflowLinkManager applications={applications} />
          </Box>
        </Fade>
      )}
    </Container>
  );
};

export default InflowStatsPage;
